import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { SerieDTO } from "./SerieDTO";
import type { SerieRecord } from "./SerieRecord";
import { convertIdFilter } from "../../utils/filterUtils";
import { PublicationStatus } from "../../model/publication-status";
import { ContentStatus } from "../../model/content-status";

const findAllTranslated = fetcher.path("/api/content/serie/findAll").method("post").create();
const getTranslated = fetcher.path("/api/content/serie/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/content/serie/{uuid}").method("put").create();
const createOne = fetcher.path("/api/content/serie/").method("post").create();
const deleteOne = fetcher.path("/api/content/serie/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/content/serie/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns SerieRecord
 */

function mapDTOToRecord(dto: SerieDTO): SerieRecord {
    // DTO from backend
    const {
        id = 0,
        uuid = "",
        name = "",
        description = "",
        status,
        publicationStatus,
        published = false,
        publicationEndedAt = null,
        publicationStartedAt = null
    } = dto;

    // Record for front-end
    return {
        shortId: id,
        id: uuid,
        name,
        description,
        publicationStatus: publicationStatus as PublicationStatus,
        status: status as ContentStatus,
        published,
        publicationEndedAt: publicationEndedAt as string,
        publicationStartedAt: publicationStartedAt as string
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns FormatDTO
 */

function mapRecordToDTO(record: SerieRecord): SerieDTO {
    // Record from front-end
    const {
        shortId = 0,
        id = "",
        name = "",
        description = "",
        publicationStatus,
        status = "draft",
        published = false,
        publicationEndedAt = null,
        publicationStartedAt = null
    } = record;

    // DTO for back-end
    return {
        id: shortId,
        name,
        uuid: id as string,
        description,
        publicationStatus,
        status,
        published,
        publicationEndedAt: publicationEndedAt as string,
        publicationStartedAt: publicationStartedAt as string
    };
}

interface SerieFilters extends BaseFilters {
    q?: string;
    id: string;
    status: ContentStatus;
    publicationStatus: PublicationStatus;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<SerieRecord>> {
    const { q: searchTerms, ...filters } = params.filter as SerieFilters;

    let parameters: FetchArgType<typeof findAllTranslated> = {
        filter: convertIdFilter(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAllTranslated(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    const result = {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };

    return result;
}

async function getOne(params: LangParams | (number | string)): Promise<SerieRecord> {
    const {
        data: { content: serieDTO }
    } = await getTranslated({
        uuid: params as string
    });
    assertIsDefined(serieDTO);

    return mapDTOToRecord({ id: serieDTO.id ?? 0, ...serieDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<SerieRecord>> {
    const {
        data: { content: serieDTOs }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(serieDTOs);

    return { data: await Promise.all(serieDTOs.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<SerieRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<SerieRecord>>): Promise<UpdateResult<SerieRecord>> {
    const payload = mapRecordToDTO(params.data as SerieRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const serieDTO = data.content;
    assertIsDefined(serieDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...serieDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<SerieRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.name);
    const parameters = { ...payload };
    delete (parameters as any)["id"];
    delete (parameters as any)["uuid"];
    const { data } = await createOne(parameters);
    const serieDTO = data.content;

    assertIsDefined(serieDTO);

    return { data: mapDTOToRecord({ id: serieDTO.id as number, ...serieDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<SerieRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as SerieRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<SerieRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const SerieDataProvider: MpmDataProvider<SerieRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne,
    update,
    updateMany
};
